import { useTraduccion, type Clave } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, IconoMas, Menu, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useTituloPantalla } from '../../layout/titulo';
import { ETAPAS, useCambiosEstados, useEstadosDeOrden, type Estado, type Etapa } from './datos';
import { NOMBRE_DE_ETAPA, VentanaEstado, type Edicion } from './VentanaEstado';

// «Estados de la orden» (O-13, #145). No hay frame en el Figma: se decidió una
// lista **agrupada por etapa** —abierta, en curso, terminada, entregada,
// anulada—, con las filas de «Marcas y modelos» y las flechas de orden de
// «Tipos de activo». Las flechas mueven dentro de la etapa; cambiar de etapa es
// de la ventana de edición, y lo lleva al final de la nueva.
//
// Nada se elimina: las órdenes viejas apuntan a sus estados, así que lo que ya no
// se usa se desactiva. Lo que el dominio no deja —desactivar el inicial o el
// último de su etapa— lo dice la API, y se muestra arriba como cualquier error.

/** Lo que dice el ⓘ de cada etapa: qué es y qué pide. */
const INFO_DE_ETAPA: Record<Etapa, Clave> = {
  open: 'admin.estados.etapa.open.info',
  inProgress: 'admin.estados.etapa.inProgress.info',
  completed: 'admin.estados.etapa.completed.info',
  closed: 'admin.estados.etapa.closed.info',
  cancelled: 'admin.estados.etapa.cancelled.info',
};

export function ListadoEstadosDeOrden() {
  const { t } = useTraduccion();
  const cambios = useCambiosEstados();
  const consulta = useEstadosDeOrden();
  const [edicion, setEdicion] = useState<Edicion>(null);
  const { hacer, problema, ocupado } = useAccionDeFila();
  useTituloPantalla(t('admin.estados.titulo'));

  const estados = consulta.data ?? [];
  // La API ya los manda en orden y numerados siguiendo las etapas; agrupar solo los reparte.
  const grupos = ETAPAS.map((etapa) => ({ etapa, estados: estados.filter((e) => e.category === etapa) }));

  /** Sube o baja un estado un lugar dentro de su etapa. Se manda el orden de todos. */
  function mover(etapa: Etapa, indice: number, hacia: -1 | 1) {
    const orden = grupos.map((g) => g.estados.map((e) => String(e.id)));
    const grupo = orden[ETAPAS.indexOf(etapa)];
    const a = grupo?.[indice];
    const b = grupo?.[indice + hacia];
    if (!grupo || a === undefined || b === undefined) return;
    grupo[indice] = b;
    grupo[indice + hacia] = a;
    void hacer(cambios.reordenar(orden.flat()), t('admin.estados.aviso.reordenado'));
  }

  function opciones(e: Estado): ElementoMenu[] {
    const id = String(e.id);
    const nombre = { nombre: e.name };
    return [
      { etiqueta: t('admin.estados.menu.editar'), onSeleccionar: () => setEdicion({ tipo: 'editar', estado: e }) },
      // Solo uno abierto y activo puede ser el inicial; al que ya lo es no se le ofrece.
      ...(e.category === 'open' && e.isActive && !e.isInitial
        ? [
            {
              etiqueta: t('admin.estados.menu.inicial'),
              onSeleccionar: () => void hacer(cambios.nombrarInicial(id), t('admin.estados.aviso.inicial', nombre)),
            },
          ]
        : []),
      e.isActive
        ? {
            etiqueta: t('admin.estados.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarActivacion(id, false), t('admin.estados.aviso.desactivado', nombre)),
          }
        : {
            etiqueta: t('admin.estados.menu.activar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarActivacion(id, true), t('admin.estados.aviso.activado', nombre)),
          },
    ];
  }

  return (
    <>
      <div className="titulo-con-ayuda">
        <h1>{t('admin.estados.titulo')}</h1>
        <Ayuda tema={t('admin.estados.titulo')} texto={t('admin.estados.info')} tono="titulo" />
      </div>

      {problema && <AvisoError aviso={{ titulo: t('admin.estados.error.titulo'), texto: problema }} />}

      {consulta.isPending ? (
        <p className="marcas__nota">{t('app.cargando')}</p>
      ) : consulta.isError ? (
        <div className="marcas__nota">
          <p>{t('ui.listado.error.texto')}</p>
          <Boton variante="secundario" onClick={() => void consulta.refetch()}>
            {t('app.reintentar')}
          </Boton>
        </div>
      ) : (
        <div className="estados">
          {grupos.map(({ etapa, estados: delGrupo }) => (
            <section key={etapa} className="tipos__seccion" aria-labelledby={`etapa-${etapa}`}>
              <div className="tipos__seccion-cabecera">
                <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
                  <h2 id={`etapa-${etapa}`}>{t(NOMBRE_DE_ETAPA[etapa])}</h2>
                  <Ayuda tema={t(NOMBRE_DE_ETAPA[etapa])} texto={t(INFO_DE_ETAPA[etapa])} tono="titulo" />
                </div>
                <Boton variante="texto" icono={<IconoMas />} onClick={() => setEdicion({ tipo: 'nuevo', etapa })}>
                  {t('admin.estados.agregar')}
                </Boton>
              </div>

              {delGrupo.length === 0 ? (
                <p className="marcas__nota">{t('admin.estados.etapaVacia')}</p>
              ) : (
                <ol className="marcas__lista">
                  {delGrupo.map((e, i) => (
                    <li key={String(e.id)} className="marcas__fila tipos__campo">
                      <span className="tipos__mover">
                        <Boton
                          variante="texto"
                          soloIcono
                          disabled={ocupado || i === 0}
                          aria-label={t('admin.estados.subir', { nombre: e.name })}
                          onClick={() => mover(etapa, i, -1)}
                        >
                          ↑
                        </Boton>
                        <Boton
                          variante="texto"
                          soloIcono
                          disabled={ocupado || i === delGrupo.length - 1}
                          aria-label={t('admin.estados.bajar', { nombre: e.name })}
                          onClick={() => mover(etapa, i, 1)}
                        >
                          ↓
                        </Boton>
                      </span>
                      <span className="marcas__elegir marcas__elegir--fijo">
                        <span className="estados__nombre">
                          <span
                            className="estados__punto"
                            style={e.color ? { background: e.color } : undefined}
                            aria-hidden="true"
                          />
                          <span className="marcas__nombre">{e.name}</span>
                        </span>
                        <span className="marcas__detalle">
                          {e.isInitial && <Chip tono="marca">{t('admin.estados.inicial')}</Chip>}
                          {/* En entregada y anulada congela siempre: lo dice el ⓘ de la etapa, no cada fila. */}
                          {e.category === 'completed' && e.isFinal && (
                            <Chip tono="neutro">{t('admin.estados.congela')}</Chip>
                          )}
                          {!e.isActive && <Chip>{t('admin.estados.inactivo')}</Chip>}
                          <span className="marcas__conteo">{t('admin.estados.conteoOrdenes', { n: e.orderCount })}</span>
                        </span>
                      </span>
                      <MenuFila elementos={opciones(e)} />
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}
        </div>
      )}

      <VentanaEstado edicion={edicion} onCerrar={() => setEdicion(null)} />
    </>
  );
}

/** El «•••» de cada fila, el mismo del listado. */
function MenuFila({ elementos }: { elementos: readonly ElementoMenu[] }) {
  const { t } = useTraduccion();
  return (
    <Menu
      elementos={elementos}
      disparador={({ alternar, atributos }) => (
        <button
          type="button"
          className="ui-tabla__mas"
          aria-label={t('ui.listado.accionesFila')}
          onClick={alternar}
          {...atributos}
        >
          •••
        </button>
      )}
    />
  );
}
